import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../api/axiosConfig';
import './ManagementPages.css';
import './ValidationPage.css';
import './VisitDetailPage.css';

// Journée des hôtesses pour leur responsable (sa base uniquement, filtrée par le serveur)
// et pour l'administrateur. Présentation reprise de ControlesTerrainPage.

// Les heures viennent du téléphone de l'hôtesse (heure locale) : on les lit telles quelles
// plutôt que de laisser le navigateur les convertir
const heure = (iso) => (iso ? iso.slice(11, 16) : '—');

const STATUTS = {
  CONFIRME: { libelle: 'Confirmé', classe: 'valide' },
  EN_RETARD: { libelle: 'En retard', classe: 'pending' },
  HORS_SITE: { libelle: 'Hors site', classe: 'rejete' },
  MANQUE: { libelle: 'Non confirmé', classe: 'rejete' },
  A_VENIR: { libelle: 'À venir', classe: 'soumis' },
};

const aujourdhui = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function SuiviHotessesPage() {
  const estAdmin = (localStorage.getItem('userRole') || '').toLowerCase() === 'administrateur';
  const [journees, setJournees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [jour, setJour] = useState(aujourdhui());
  const [base, setBase] = useState('');
  const [photo, setPhoto] = useState(null);

  const charger = useCallback(async () => {
    setIsLoading(true);
    setErreur(null);
    try {
      const params = new URLSearchParams({ date_jour: jour });
      if (estAdmin && base) params.append('base', base);
      const reponse = await axiosInstance.get(`/hotesses/suivi?${params.toString()}`);
      setJournees(Array.isArray(reponse.data) ? reponse.data : []);
    } catch (e) {
      setErreur(e.response?.data?.detail || "Impossible de charger le suivi. Réessayez dans un instant.");
    } finally {
      setIsLoading(false);
    }
  }, [jour, base, estAdmin]);

  useEffect(() => { charger(); }, [charger]);

  // Rafraîchissement automatique : les confirmations arrivent au fil de la journée
  useEffect(() => {
    if (jour !== aujourdhui()) return undefined;
    const minuteur = setInterval(charger, 120000);
    return () => clearInterval(minuteur);
  }, [jour, charger]);

  const presentes = journees.filter(j => j.presences.length > 0).length;
  const avecAlerte = journees.filter(j => j.alertes.length > 0).length;

  const champ = { padding: '7px 10px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px' };
  const libelle = { fontSize: '12px', fontWeight: '600', color: '#495057' };
  const colonne = { display: 'flex', flexDirection: 'column', gap: '4px' };
  const compteur = (valeur, texte, couleur) => (
    <div style={{ background: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '12px 18px', minWidth: '140px' }}>
      <div style={{ fontSize: '26px', fontWeight: 700, color: couleur }}>{valeur}</div>
      <div style={{ fontSize: '13px', color: '#6c757d' }}>{texte}</div>
    </div>
  );

  return (
    <div className="management-page" style={{ padding: '20px' }}>
      <h1 style={{ color: '#2c3e50', margin: 0 }}>Présence des hôtesses</h1>
      <p style={{ color: '#6c757d', marginTop: '6px' }}>
        Arrivée vérifiée sur place, photo de présentation, messages de présence et départ.
      </p>

      <div style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
          <div style={colonne}>
            <label style={libelle}>Jour</label>
            <input type="date" style={champ} value={jour} onChange={(e) => setJour(e.target.value || aujourdhui())} />
          </div>
          {estAdmin && (
            <div style={colonne}>
              <label style={libelle}>Base</label>
              <select style={{ ...champ, minWidth: '140px' }} value={base} onChange={(e) => setBase(e.target.value)}>
                <option value="">Toutes les bases</option>
                <option value="DOUALA">DOUALA</option>
                <option value="YAOUNDE">YAOUNDE</option>
                <option value="BAFOUSSAM">BAFOUSSAM</option>
              </select>
            </div>
          )}
          <button onClick={charger}
            style={{ padding: '7px 18px', background: '#0d6efd', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
            Actualiser
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
        {compteur(journees.length, 'hôtesses', '#2c3e50')}
        {compteur(presentes, 'arrivées', '#28a745')}
        {compteur(journees.length - presentes, 'pas arrivées', '#dc3545')}
        {compteur(avecAlerte, 'avec une alerte', '#fd7e14')}
      </div>

      {erreur && <p className="no-data-message" style={{ color: '#dc3545' }}>{erreur}</p>}

      <div className="management-table-container">
        {isLoading ? (
          <p style={{ textAlign: 'center', padding: '40px' }}>Chargement...</p>
        ) : journees.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Hôtesse</th>
                <th>Supermarché</th>
                <th>Arrivée → départ</th>
                <th>Photo</th>
                <th>Messages de présence</th>
                <th>Contrôles</th>
                <th>Alertes</th>
              </tr>
            </thead>
            <tbody>
              {journees.map(j => {
                const photos = j.presences.filter(p => p.photo_url);
                return (
                  <tr key={j.hotesse_id}>
                    <td><strong>{j.nom}</strong>{estAdmin && j.base ? <div style={{ fontSize: '12px', color: '#6c757d' }}>{j.base}</div> : null}</td>
                    <td>{j.client_nom || <span style={{ color: '#dc3545' }}>Non affectée</span>}</td>
                    <td>
                      {j.presences.length === 0 ? '—' : j.presences.map(p => (
                        <div key={p.id} style={{ whiteSpace: 'nowrap' }}>
                          {heure(p.heure_arrivee)} → {p.heure_depart ? heure(p.heure_depart) : 'sur place'}
                          {p.motif_depart === 'SORTIE_SITE' && <span style={{ color: '#dc3545' }}> (sortie)</span>}
                        </div>
                      ))}
                    </td>
                    <td>
                      {photos.length === 0 ? '—' : photos.map(p => (
                        <img key={p.id} src={`${axiosInstance.defaults.baseURL}${p.photo_url}`} alt="Présentation"
                          onClick={() => setPhoto(p.photo_url)}
                          style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '4px', cursor: 'pointer', marginRight: '4px' }} />
                      ))}
                    </td>
                    <td>
                      {j.verifications.length === 0 ? '—' : j.verifications.map(v => {
                        const s = STATUTS[v.statut] || STATUTS.A_VENIR;
                        return (
                          <div key={v.id} style={{ marginBottom: '4px', whiteSpace: 'nowrap' }}>
                            {heure(v.heure_prevue)} <span className={`status-badge ${s.classe}`}>{s.libelle}</span>
                          </div>
                        );
                      })}
                    </td>
                    <td>{j.nb_controles || '—'}</td>
                    <td>
                      {j.alertes.length === 0
                        ? <span className="status-badge valide">RAS</span>
                        : j.alertes.map((a, i) => <div key={i} style={{ color: '#dc3545', fontSize: '13px' }}>⚠ {a}</div>)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="no-data-message">Aucune hôtesse active pour cette base.</p>
        )}
      </div>

      {photo && (
        <div onClick={() => setPhoto(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, cursor: 'pointer' }}>
          <img src={`${axiosInstance.defaults.baseURL}${photo}`} alt="Présentation" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: '8px' }} />
        </div>
      )}
    </div>
  );
}

export default SuiviHotessesPage;
